import {SpineAnimation} from '@fantasmagames/core/src/display/SpineAnimation';
import {SpriteButton} from '@fantasmagames/core/src/display/SpriteButton';
import {Spine} from '@fantasmagames/core/src/spine/Spine';
import {AnimationNames} from '@fantasmagames/slot/src/animations/AnimationNames';
import {View} from '@fantasmagames/slot/src/common/view/View';
import {MainMenuViewEvents} from '@fantasmagames/slot/src/modules/mainmenu/MainMenuViewEvents';
import {GenericButton} from '@fantasmagames/slot/src/modules/popupmenus/GenericButton';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';

export class MainMenuView extends View {
	protected _mainMenuLayout: Spine;

	protected _confirmButton: SpriteButton;
	protected _closeButton: SpriteButton;
	protected _paytableButton: GenericButton;
	protected _settingsButton: GenericButton;
	protected _infoButton: GenericButton;
	protected _lobbyButton: SpriteButton;

	protected _menuTitle: PIXI.Text;
	protected _menuTitleContainer: PIXI.Container;

	protected _titleTextStyle: PIXI.TextStyle;
	protected _breadTextStyle: PIXI.TextStyle;

	protected _backgroundObject: PIXI.Container;

	protected _rules: PIXI.Container;
	protected _settings: PIXI.Container;
	protected _paytable: PIXI.Container;

	// TODO: Retrieve link from init data or casino.
	protected _lobbyLink: string = 'https://www.fantasmagames.com/';
	protected _visible: boolean;
	protected _mainAnimation: SpineAnimation;

	protected _menuTitleBackground: PIXI.Container;

	public init(): void {
		super.init();

		this.setupMainMenu();
		this.setupButtons();
		this.setupTextStyles();
		this.setupTextObjects();
		this.addExtras();
	}

	public translate(): void {
		let paytableText: string = this.translator.getObject('BaseStrings', 'generic', 'g_paytable') || 'PAYTABLE';
		paytableText = paytableText.toUpperCase();
		this._menuTitle.text = paytableText.toUpperCase();
		this.updateTitleScale();

		this.postTranslate();
	}

	public showMainMenu(): void {
		this._mainMenuLayout.visible = true;
		this._visible = true;

		const animation = this.playMainLayoutAnimation(AnimationNames.MAIN_MENU.Name.ENTER, false);
		animation.setOnComplete(this.playIdleMainMenuVisible.bind(this));
	}

	public hideMainMenu(): void {
		const animation = this.playMainLayoutAnimation(AnimationNames.MAIN_MENU.Name.EXIT, false);
		animation.setOnComplete(this.playIdleMainMenuHidden.bind(this));
	}

	protected postTranslate(): void {
		this._paytableButton.forceClicked();
		this._paytableButton.setSelected();
	}

	protected addExtras(): void {}

	protected playIdleMainMenuHidden(): void {
		this.playMainLayoutAnimation(AnimationNames.MAIN_MENU.Name.IDLE_HIDDEN, true);
		this._mainMenuLayout.visible = false;
		this._visible = false;
	}

	protected playIdleMainMenuVisible(): void {
		this.playMainLayoutAnimation(AnimationNames.MAIN_MENU.Name.IDLE_VISIBLE, true);
	}

	protected setupMainMenu(): void {
		this._mainMenuLayout = new Spine(this.resources.getSpine('MAIN_MENU.json'));
		this.playIdleMainMenuHidden();

		this._mainMenuLayout.position.set(this.resolutionManager.halfResolution.width, this.resolutionManager.halfResolution.height);
		this.container.addChild(this._mainMenuLayout);

		this._mainMenuLayout.slotContainers[this._mainMenuLayout.skeleton.findSlotIndex('BACKGROUND_SOLID')].interactive = true;
		this._backgroundObject = this._mainMenuLayout.slotContainers[this._mainMenuLayout.skeleton.findSlotIndex('MENU_ELEMENT')];
		this._backgroundObject.interactive = true;
		this._backgroundObject.children[0].visible = false;

		this._menuTitleBackground = this._mainMenuLayout.slotContainers[
			this._mainMenuLayout.skeleton.findSlotIndex('MENU_TITLE_BACKGROUND')
		];

		this._menuTitleContainer = this._mainMenuLayout.slotContainers[this._mainMenuLayout.skeleton.findSlotIndex('MENU_TITLE')];
		this._menuTitleContainer.children[0].visible = false;
	}

	protected setupTextStyles(): void {
		this._titleTextStyle = this.textStyles.mainMenuTitle;
		this._titleTextStyle.wordWrapWidth = this.resolutionManager.resolution.width;

		this._breadTextStyle = this.textStyles.mainMenuBread;
		this._breadTextStyle.wordWrapWidth = this.resolutionManager.resolution.width;
	}

	protected setupTextObjects(): void {
		// let paytableText: string = this.translator.getObject('BaseStrings', 'generic', 'g_paytable') || 'PAYTABLE';
		// paytableText = paytableText.toUpperCase();
		this._menuTitle = new PIXI.Text('', this._titleTextStyle);
		this._menuTitleContainer.addChild(this._menuTitle);
		this._menuTitle.anchor.set(0.5, 0.5);
		this._menuTitle.transform = this._menuTitleContainer.children[0].transform;
		this._menuTitleContainer.children[0].destroy();
		// this._menuTitleContainer.children[0].visible = false;
	}

	protected onCloseButtonClicked(): void {
		this.soundPlayer.play('buttonClick');

		this.emit(MainMenuViewEvents.MAIN_MENU_CLOSE_CLICKED);
	}

	protected onPaytableButtonClicked(isRealClick: boolean): void {
		if (isRealClick) {
			this.soundPlayer.play('buttonClick');
		}

		this.emit(MainMenuViewEvents.MAIN_MENU_PAYTABLE_CLICKED);
		this._settingsButton.resetState();
		this._infoButton.resetState();
		this.showPaytable();
	}

	protected onSettingsButtonClicked(): void {
		this.soundPlayer.play('buttonClick');

		this.emit(MainMenuViewEvents.MAIN_MENU_SETTINGS_CLICKED);
		this._infoButton.resetState();
		this._paytableButton.resetState();
		this.showSettings();
	}

	protected onInfoButtonClicked(): void {
		this.soundPlayer.play('buttonClick');

		this.emit(MainMenuViewEvents.MAIN_MENU_INFO_CLICKED);
		this._settingsButton.resetState();
		this._paytableButton.resetState();
		this.showInfo();
	}

	protected onLobbyButtonClicked(): void {
		this.soundPlayer.play('buttonClick');

		this.emit(MainMenuViewEvents.MAIN_MENU_LOBBY_CLICKED);
		this.openLobbyLink();
	}

	protected updateTitleScale(): void {
		const maxSize = {
			x: this._titleTextStyle.wordWrapWidth,
			y: (this._menuTitleBackground.children[0] as PIXI.Sprite).height,
		};

		if (this._menuTitle.width > maxSize.x || this._menuTitle.height > maxSize.y) {
			GameUtils.correctSpriteScale(this._menuTitle, maxSize);
			// this._menuTitle.scale.y *= -1;
		}
	}

	protected showPaytable(): void {
		const paytableText: string = this.translator.getObject('BaseStrings', 'generic', 'g_paytable') || 'PAYTABLE';
		this._menuTitle.text = paytableText.toUpperCase();
		this.updateTitleScale();
		this.showCloseButton();

		this.emit(MainMenuViewEvents.MAIN_MENU_PAYTABLE_OPEN);
	}

	protected showSettings(): void {
		const settingsText: string = this.translator.getObject('BaseStrings', 'generic', 'g_settings') || 'SETTINGS';
		this._menuTitle.text = settingsText.toUpperCase();
		this.updateTitleScale();

		this.showConfirmButton();
		this.emit(MainMenuViewEvents.MAIN_MENU_SETTINGS_OPEN);
	}

	protected showInfo(): void {
		const text: string = this.translator.getObject('BaseStrings', 'generic', 'g_gamerules') || 'GAME RULES';
		this._menuTitle.text = text.toUpperCase();
		this.updateTitleScale();
		this.showCloseButton();

		this.emit(MainMenuViewEvents.MAIN_MENU_INFO_OPEN);
	}

	protected hidePaytable(): void {
		this.emit(MainMenuViewEvents.MAIN_MENU_PAYTABLE_CLOSE);
	}

	protected hideSettings(): void {
		this.showCloseButton();
		this.emit(MainMenuViewEvents.MAIN_MENU_SETTINGS_CLOSE);
	}

	protected showCloseButton(): void {
		this._closeButton.visible = true;
		this._confirmButton.visible = false;
	}

	protected showConfirmButton(): void {
		this._confirmButton.visible = true;
		this._closeButton.visible = false;
	}

	protected hideInfo(): void {
		this.emit(MainMenuViewEvents.MAIN_MENU_INFO_CLOSE);
	}

	public openLobbyLink(): void {
		window.open(this.lobbyLink, '_blank');
		this.emit(MainMenuViewEvents.MAIN_MENU_LOBBY_OPEN);
	}

	protected playMainLayoutAnimation(animationName: string, loop: boolean, stopOnComplete: boolean = true): SpineAnimation {
		if (this._mainAnimation) {
			this._mainAnimation.stop();
			this.updateManager.remove(this._mainAnimation);
		}

		this._mainAnimation = new SpineAnimation(this._mainMenuLayout, animationName, loop, true);
		if (stopOnComplete) {
			this._mainAnimation.setOnComplete(this._mainAnimation.stop.bind(this));
		}

		this.updateManager.add(this._mainAnimation);
		this._mainAnimation.start();

		return this._mainAnimation;
	}

	protected setupButtons(): void {
		this._confirmButton = new SpriteButton(this.onCloseButtonClicked.bind(this));
		this._closeButton = new SpriteButton(this.onCloseButtonClicked.bind(this));
		this._paytableButton = new GenericButton(this.onPaytableButtonClicked.bind(this));
		this._settingsButton = new GenericButton(this.onSettingsButtonClicked.bind(this));
		this._infoButton = new GenericButton(this.onInfoButtonClicked.bind(this));
		this._lobbyButton = new SpriteButton(this.onLobbyButtonClicked.bind(this));

		this._confirmButton.setFramesNames(this.resources, 'HUD.json', 'CONFIRM.png', 'CONFIRM_CLICKED.png', 'CONFIRM.png', 'CONFIRM.png');

		this._closeButton.setFramesNames(
			this.resources,
			'HUD.json',
			'EXIT_BUTTON.png',
			'EXIT_BUTTON_CLICKED.png',
			'EXIT_BUTTON.png',
			'EXIT_BUTTON.png',
		);

		this._lobbyButton.setFramesNames(
			this.resources,
			'HUD.json',
			'HOME_BUTTON.png',
			'HOME_BUTTON_CLICKED.png',
			'HOME_BUTTON.png',
			'HOME_BUTTON.png',
		);

		this._infoButton.setFramesNames(
			this.resources,
			'HUD.json',
			'INFO_BUTTON.png',
			'INFO_BUTTON_CLICKED.png',
			'INFO_BUTTON.png',
			'INFO_BUTTON.png',
		);

		this._settingsButton.setFramesNames(
			this.resources,
			'HUD.json',
			'SETTINGS_BUTTON.png',
			'SETTINGS_BUTTON_CLICKED.png',
			'SETTINGS_BUTTON.png',
			'SETTINGS_BUTTON.png',
		);

		this._paytableButton.setFramesNames(
			this.resources,
			'HUD.json',
			'PAYTABLE_BUTTON.png',
			'PAYTABLE_BUTTON_CLICKED.png',
			'PAYTABLE_BUTTON.png',
			'PAYTABLE_BUTTON.png',
		);

		////////////////////////////////////////////////////////////////////
		// Get Skeleton Slots for replacement.
		////////////////////////////////////////////////////////////////////

		const closeButtonSlot = this._mainMenuLayout.slotContainers[this._mainMenuLayout.skeleton.findSlotIndex('EXIT_BUTTON')];
		const lobbyButtonSlot = this._mainMenuLayout.slotContainers[this._mainMenuLayout.skeleton.findSlotIndex('HOME_BUTTON')];
		const paytableButtonSlot = this._mainMenuLayout.slotContainers[this._mainMenuLayout.skeleton.findSlotIndex('PAYTABLE_BUTTON')];
		const settingsButtonSlot = this._mainMenuLayout.slotContainers[this._mainMenuLayout.skeleton.findSlotIndex('SETTINGS_BUTTON')];
		const infoButtonSlot = this._mainMenuLayout.slotContainers[this._mainMenuLayout.skeleton.findSlotIndex('INFO_BUTTON')];

		////////////////////////////////////////////////////////////////////
		// Add the new buttons to their appropriate slot.
		////////////////////////////////////////////////////////////////////

		closeButtonSlot.addChild(this._closeButton);
		this._closeButton.transform = closeButtonSlot.children[0].transform;
		closeButtonSlot.children[0].destroy();

		closeButtonSlot.addChild(this._confirmButton);
		this._confirmButton.transform = closeButtonSlot.children[0].transform;

		paytableButtonSlot.addChild(this._paytableButton);
		this._paytableButton.transform = paytableButtonSlot.children[0].transform;
		paytableButtonSlot.children[0].destroy();

		settingsButtonSlot.addChild(this._settingsButton);
		this._settingsButton.transform = settingsButtonSlot.children[0].transform;
		settingsButtonSlot.children[0].destroy();

		lobbyButtonSlot.addChild(this._lobbyButton);
		this._lobbyButton.transform = lobbyButtonSlot.children[0].transform;
		lobbyButtonSlot.children[0].destroy();

		infoButtonSlot.addChild(this._infoButton);
		this._infoButton.transform = infoButtonSlot.children[0].transform;
		infoButtonSlot.children[0].destroy();
	}

	protected createAndSetupMenuContainer(menuItem: PIXI.Container): void {
		const container = new PIXI.Sprite();
		container.addChild(menuItem);
		this._backgroundObject.addChild(container);
		container.transform = this._backgroundObject.children[0].transform;

		menuItem.position.x -= this.resolutionManager.halfResolution.width;
		menuItem.position.y -= this.resolutionManager.halfResolution.height;

		(menuItem as PIXI.Sprite).anchor.set(0.5, 0.5);
	}

	public set lobbyLink(newLink: string) {
		this._lobbyLink = newLink;
	}

	public get lobbyLink(): string {
		return this._lobbyLink;
	}

	public set paytable(paytable: PIXI.Container) {
		this._paytable = paytable;
		this.createAndSetupMenuContainer(paytable);
	}

	public set settings(settings: PIXI.Container) {
		this._settings = settings;
		this.createAndSetupMenuContainer(settings);
	}

	public set rules(rules: PIXI.Container) {
		this._rules = rules;
		this.createAndSetupMenuContainer(rules);
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/mainmenu/MainMenuView.ts